import React, { createContext, useCallback, useState } from 'react';

/**
 * Interface for the data refresh context.
 * Provides the current data refresh counter and a function to trigger data refresh.
 */
interface DataRefreshContextProps {
  dataRefreshCounter: number;
  triggerDataRefresh: () => void;
}

/**
 * Create the context with default values.
 */
const DataRefreshContext = createContext<DataRefreshContextProps>({
  dataRefreshCounter: 0,
  triggerDataRefresh: () => {
    throw new Error('Not implemented');
  },
});

/**
 * Provider component for the DataRefreshContext.
 * Manages the dataRefreshCounter state and provides a function to trigger data refresh.
 */
export const DataRefreshProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State variable to track data refresh counts
  const [dataRefreshCounter, setDataRefreshCounter] = useState(0);

  /**
   * Function to trigger data refresh.
   * Increments the dataRefreshCounter, which can be used by consumers to detect changes.
   */
  const triggerDataRefresh = useCallback(() => {
    setDataRefreshCounter((prev) => prev + 1);
  }, []);

  return (
    <DataRefreshContext.Provider
      value={{ dataRefreshCounter, triggerDataRefresh }}
    >
      {children}
    </DataRefreshContext.Provider>
  );
};

export default DataRefreshContext;
